import type { OrderStats, OrderTypeStat, OrderType, FulfilmentSlice } from '../../types/order'
import { formatEuros } from '../../lib/format'
import { channelColor, channelLabel, ProductMarginCell } from './SourceBreakdown'
import { salesCents, deliveryFeesCents, avgOrderCents } from './salesBasis'

// ─── Sales by fulfilment ────────────────────────────────────────────────────────
// The second dimension of orderStats, on its own: HOW the order reached the
// customer (dine-in / takeaway / delivery), across every channel together. One
// tile per type; the Delivery tile additionally shows the same figures per
// channel that delivered (Webshop, Thuisbezorgd, Uber Eats — and Counter or
// Kiosk if a delivery was ever keyed there) next to the delivery total:
// byType[DELIVERY].byChannel, Σ == the row.
//
// Money basis follows the channel table (salesBasis.ts): ex-VAT sales and
// product margin for financial users, incl-VAT sales otherwise. Delivery fees
// are INSIDE sales ("of which") but never in the product margin or the average
// order; what they made after the platform's cut is its own row, not a step
// towards the margin.

/** The three fulfilment types in display order, with the label the dashboard uses. */
export const FULFILMENT_TYPES: { type: OrderType; label: string; color: string }[] = [
  { type: 'DINE_IN', label: 'Dine-in', color: '#CA4330' },
  { type: 'TAKEAWAY', label: 'Takeaway', color: '#853F82' },
  { type: 'DELIVERY', label: 'Delivery', color: '#4F46E5' },
]

// A type with no orders is absent from byType (sparse); the tile still renders.
const EMPTY: FulfilmentSlice = {
  orderCount: 0, grossRevenueCents: 0, discountCents: 0, packagingFeeCents: 0, deliveryFeeCents: 0,
  deliveryFeeExVatCents: null, turnoverExVatCents: null, productSalesExVatCents: null,
  contributionMarginCents: null, deliveryResultCents: null,
}

interface Row {
  label: string
  tone: string
  cell: (s: FulfilmentSlice) => React.ReactNode
}

export const FulfilmentBreakdown = ({ stats, canFin }: { stats: OrderStats; canFin: boolean }) => {
  const sales = (s: FulfilmentSlice) => salesCents(s, canFin)
  const avgOrder = (s: FulfilmentSlice) => avgOrderCents(s, canFin)
  const productMargin = (s: FulfilmentSlice) => (
    <ProductMarginCell cents={s.contributionMarginCents ?? 0} productSales={s.productSalesExVatCents ?? 0} />
  )
  const dash = <span className="text-gray-400">—</span>
  // Gross (incl. VAT) whatever the tile's basis — the API has no ex-VAT discount figure.
  const discounts = (s: FulfilmentSlice) => (s.discountCents > 0 ? formatEuros(s.discountCents) : dash)
  // Every share on this card is "of ALL sales" — the tiles add up to 100% and
  // the Delivery table's channel cells add up to the Delivery total's cell.
  const totalSales = salesCents(stats, canFin)
  const sharePct = (s: FulfilmentSlice) => (totalSales > 0 ? (sales(s) / totalSales) * 100 : 0)
  const share = (s: FulfilmentSlice) => (totalSales > 0 ? `${Math.round(sharePct(s))}%` : '—')
  const byType = (type: OrderType): OrderTypeStat | undefined => stats.byType?.find((t) => t.orderType === type)

  const deliveryRows: Row[] = [
    { label: canFin ? 'Sales (ex. VAT)' : 'Sales (incl. VAT)', tone: 'font-semibold text-gray-900', cell: (s) => formatEuros(sales(s)) },
    { label: 'of which delivery fees', tone: 'text-gray-500', cell: (s) => (deliveryFeesCents(s, canFin) > 0 ? formatEuros(deliveryFeesCents(s, canFin)) : dash) },
    { label: 'Share of all sales', tone: 'text-gray-500', cell: (s) => share(s) },
    ...(canFin ? [{ label: 'Product margin', tone: 'font-semibold', cell: productMargin }] : []),
    { label: 'Avg. order size', tone: 'text-gray-700', cell: (s) => formatEuros(avgOrder(s)) },
    { label: 'Discounts applied (incl. VAT)', tone: 'text-gray-700', cell: discounts },
    // Delivery fees minus the platform commission charged on them. Its own
    // figure — that commission is already inside the product margin above.
    ...(canFin ? [{ label: 'Made on delivery', tone: 'font-semibold text-gray-900', cell: (s: FulfilmentSlice) => ((s.deliveryFeeExVatCents ?? 0) > 0 ? formatEuros(s.deliveryResultCents ?? 0) : dash) }] : []),
    { label: 'Orders', tone: 'font-semibold text-gray-900', cell: (s) => s.orderCount },
  ]

  const ds = stats.deliverySpeed

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {FULFILMENT_TYPES.map(({ type, label, color }) => {
        const row = byType(type)
        const s: FulfilmentSlice = row ?? EMPTY
        const isDelivery = type === 'DELIVERY'
        const channels = isDelivery ? [...(row?.byChannel ?? [])].sort((a, b) => a.sortOrder - b.sortOrder) : []

        return (
          <div
            key={type}
            className={`rounded-xl border p-5 flex flex-col gap-4 ${isDelivery ? 'lg:col-span-2 border-indigo-100 bg-indigo-50/30' : 'border-gray-100'}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-800">{label}</span>
              <span className="text-xs text-gray-400">{s.orderCount} orders</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-gray-900 leading-none">{formatEuros(sales(s))}</span>
                <span className="text-sm font-semibold text-gray-500">{share(s)}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${sharePct(s)}%`, backgroundColor: color }} />
              </div>
            </div>

            {!isDelivery ? (
              <div className="space-y-1.5 text-sm pt-2.5 border-t border-gray-100">
                {canFin && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Product margin</span>
                    <span className="font-semibold">{productMargin(s)}</span>
                  </div>
                )}
                <div className="flex justify-between"><span className="text-gray-500">Avg. order size</span><span className="text-gray-700 tabular-nums">{formatEuros(avgOrder(s))}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Discounts applied (incl. VAT)</span><span className="text-gray-700 tabular-nums">{discounts(s)}</span></div>
              </div>
            ) : (
              <>
                {/* The same figures, total + per channel the order was placed on.
                    A Thuisbezorgd order our own driver delivers stays under
                    Thuisbezorgd, with the fee we collected on it. */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-indigo-100">
                        <th className="py-1.5 pr-2" />
                        <th className="py-1.5 px-2 text-right font-semibold text-gray-900 border-r border-indigo-100 bg-indigo-50/60">Total</th>
                        {channels.map((c) => (
                          <th key={c.channel} className="py-1.5 px-2">
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: channelColor(c.channel) }} />
                              <span className="font-semibold text-gray-700 whitespace-nowrap">{channelLabel(c)}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryRows.map((r) => (
                        <tr key={r.label} className="border-b border-gray-100">
                          <td className="py-1.5 pr-2 text-gray-500 whitespace-nowrap">{r.label}</td>
                          <td className={`py-1.5 px-2 text-right tabular-nums border-r border-indigo-100 bg-indigo-50/60 ${r.tone}`}>{r.cell(s)}</td>
                          {channels.map((c) => (
                            <td key={c.channel} className={`py-1.5 px-2 text-right tabular-nums ${r.tone}`}>{r.cell(c)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Who drove — from the timing block, which counts both sides on one
                    basis. Not derived from the money rows: those exclude staff meals. */}
                {((ds?.orderCount ?? 0) > 0 || (ds?.platformOrderCount ?? 0) > 0) && (
                  <p className="text-xs text-gray-400">
                    Driven by our drivers: <span className="font-semibold text-gray-600">{ds.orderCount}</span>
                    <span className="mx-1.5 text-gray-300">·</span>
                    by platform couriers: <span className="font-semibold text-gray-600">{ds.platformOrderCount}</span>
                  </p>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
